import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Activity, RefreshCw, CheckCircle2, XCircle, Clock, Layers,
  AlertTriangle, Database, Zap, BarChart3, Timer, Cpu, Brain,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { EmptyState } from '@/components/system/EmptyState';
import { getPipelineStats, getEngineHealthMetrics } from '@/services/riskAnalyticsService';

interface QueueStats {
  pending: number;
  processing: number;
  processed_today: number;
}

interface SimLog {
  id: string;
  tipo: string;
  acao: string;
  created_at: string;
  dados_snapshot: Record<string, any> | null;
}

interface McRun {
  id: string;
  contract_id: string;
  status: string;
  tipo: string;
  n_simulations: number;
  created_at: string;
  completed_at: string | null;
}

interface EngineHealth {
  lastRunAt: string | null;
  failuresLast24h: number;
  avgDurationMs: number;
  nlpErrorsLast24h: number;
}

const RiskPipelineMonitor: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [queueStats, setQueueStats] = useState<QueueStats>({ pending: 0, processing: 0, processed_today: 0 });
  const [recentLogs, setRecentLogs] = useState<SimLog[]>([]);
  const [recentRuns, setRecentRuns] = useState<McRun[]>([]);
  const [engineHealth, setEngineHealth] = useState<EngineHealth | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pipelineData, healthData] = await Promise.all([
        getPipelineStats(),
        getEngineHealthMetrics(),
      ]);

      setQueueStats(pipelineData.queue);
      setRecentLogs(pipelineData.logs as SimLog[]);
      setRecentRuns(pipelineData.runs as McRun[]);
      setEngineHealth(healthData);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Pipeline monitor fetch error:', err);
      toast.error('Erro ao carregar dados do monitor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Realtime subscription for queue changes
  useEffect(() => {
    const channel = supabase
      .channel('pipeline-monitor')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'risk_monte_carlo_runs' }, () => fetchAll())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [fetchAll]);

  const statusIcon = (status: string) => {
    if (status === 'completed') return <CheckCircle2 className="h-3.5 w-3.5 text-success" />;
    if (status === 'failed' || status === 'error') return <XCircle className="h-3.5 w-3.5 text-destructive" />;
    if (status === 'running' || status === 'processing') return <Activity className="h-3.5 w-3.5 text-warning animate-pulse" />;
    return <Clock className="h-3.5 w-3.5 text-muted-foreground" />;
  };

  const statusBadge = (status: string) => {
    const variant = status === 'completed' ? 'outline' as const
      : status === 'failed' ? 'destructive' as const
      : 'secondary' as const;
    return <Badge variant={variant} className="text-xs h-5">{status}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-primary" />
          <h3 className="font-semibold text-sm">Monitor de Pipeline</h3>
          {lastRefresh && (
            <span className="text-xs text-muted-foreground">
              Atualizado: {lastRefresh.toLocaleTimeString('pt-BR')}
            </span>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading}>
          <RefreshCw className={cn('h-3.5 w-3.5 mr-1', loading && 'animate-spin')} />
          Atualizar
        </Button>
      </div>

      {/* Queue Status Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className={cn("border-l-4", queueStats.pending > 0 ? "border-l-warning" : "border-l-success/50")}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground font-medium">Fila Pendente</span>
              <Layers className="h-4 w-4 text-muted-foreground" />
            </div>
            <span className={cn("text-2xl font-bold", queueStats.pending > 0 ? "text-warning" : "text-success")}>
              {queueStats.pending}
            </span>
            {queueStats.processing > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                {queueStats.processing} em processamento
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-primary/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground font-medium">Processados Hoje</span>
              <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
            </div>
            <span className="text-2xl font-bold text-foreground">{queueStats.processed_today}</span>
          </CardContent>
        </Card>

        <Card className={cn("border-l-4", recentRuns.some(r => r.status === 'failed') ? "border-l-destructive" : "border-l-success/50")}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground font-medium">Saúde da Pipeline</span>
              {recentRuns.some(r => r.status === 'failed')
                ? <AlertTriangle className="h-4 w-4 text-destructive" />
                : <CheckCircle2 className="h-4 w-4 text-success" />
              }
            </div>
            <span className={cn("text-sm font-semibold",
              recentRuns.some(r => r.status === 'failed') ? "text-destructive" : "text-success"
            )}>
              {recentRuns.some(r => r.status === 'failed') ? 'Falhas detectadas' : 'Operacional'}
            </span>
          </CardContent>
        </Card>
      </div>

      {/* Engine Health Metrics */}
      {engineHealth && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-primary/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-muted-foreground font-medium">Última Execução MC</span>
                <Timer className="h-4 w-4 text-muted-foreground" />
              </div>
              <span className="text-sm font-semibold text-foreground">
                {engineHealth.lastRunAt
                  ? new Date(engineHealth.lastRunAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
                  : 'Nenhuma'}
              </span>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-primary/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-muted-foreground font-medium">Tempo Médio</span>
                <Cpu className="h-4 w-4 text-muted-foreground" />
              </div>
              <span className="text-sm font-semibold text-foreground">
                {engineHealth.avgDurationMs > 0
                  ? `${(engineHealth.avgDurationMs / 1000).toFixed(1)}s`
                  : '—'}
              </span>
            </CardContent>
          </Card>

          <Card className={cn("border-l-4", engineHealth.failuresLast24h > 0 ? "border-l-destructive" : "border-l-success/50")}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-muted-foreground font-medium">Falhas MC (24h)</span>
                <XCircle className="h-4 w-4 text-muted-foreground" />
              </div>
              <span className={cn("text-2xl font-bold", engineHealth.failuresLast24h > 0 ? "text-destructive" : "text-success")}>
                {engineHealth.failuresLast24h}
              </span>
            </CardContent>
          </Card>

          <Card className={cn("border-l-4", engineHealth.nlpErrorsLast24h > 0 ? "border-l-warning" : "border-l-success/50")}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-muted-foreground font-medium">Erros NLP (24h)</span>
                <Brain className="h-4 w-4 text-muted-foreground" />
              </div>
              <span className={cn("text-2xl font-bold", engineHealth.nlpErrorsLast24h > 0 ? "text-warning" : "text-success")}>
                {engineHealth.nlpErrorsLast24h}
              </span>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-primary" />
            <CardTitle className="text-sm">Simulações Recentes</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {recentRuns.length > 0 ? (
            <div className="space-y-2">
              {recentRuns.map(run => (
                <div key={run.id} className="flex items-center justify-between py-2 border-b last:border-0 text-xs">
                  <div className="flex items-center gap-2">
                    {statusIcon(run.status)}
                    <span className="font-mono text-muted-foreground">{run.id.slice(0, 8)}</span>
                    <Badge variant="outline" className="text-xs h-4">{run.tipo}</Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-muted-foreground">{run.n_simulations.toLocaleString()} iter.</span>
                    {statusBadge(run.status)}
                    <span className="text-muted-foreground w-[120px] text-right">
                      {new Date(run.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState text="Nenhuma simulação registrada." icon={Brain} className="py-4" />
          )}
        </CardContent>
      </Card>

      {/* Audit Logs */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4 text-primary" />
            <CardTitle className="text-sm">Logs de Auditoria</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {recentLogs.length > 0 ? (
            <div className="space-y-2">
              {recentLogs.map(log => (
                <div key={log.id} className="flex items-center justify-between py-2 border-b last:border-0 text-xs">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="font-medium">{log.acao}</span>
                    <Badge variant="outline" className="text-xs h-4">{log.tipo}</Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    {log.dados_snapshot && (
                      <span className="text-muted-foreground">
                        {(log.dados_snapshot as any).pending_items ?? 0} itens
                      </span>
                    )}
                    <span className="text-muted-foreground">
                      {new Date(log.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState text="Nenhum log de auditoria registrado." icon={Database} className="py-4" />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RiskPipelineMonitor;
